import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { updateUser } from "../api/mutations";
import { fetchUser } from "../api/queries";
import LanguageSelector from "../components/LanguageSelector";
import { safeSessionStorage } from "../utils/storage";

export default function TermsOfService() {
  console.log("TermsOfService: Component rendering");

  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accepted, setAccepted] = useState(false);

  // Query to fetch the latest user data
  const { data: userData, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUser,
    enabled: !!user,
  });

  console.log("TermsOfService: Component state", {
    userData: userData
      ? {
          is_tos_accepted: userData.is_tos_accepted,
          rawUserData: JSON.parse(JSON.stringify(userData)),
        }
      : null,
    isLoading,
  });

  const updateUserMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      console.log("TermsOfService: TOS acceptance updated successfully");
      const intendedPath =
        safeSessionStorage.get("intendedPath") || "/dashboard";
      safeSessionStorage.remove("intendedPath");
      navigate(intendedPath);
    },
  });

  const handleAccept = async () => {
    if (!accepted) {
      return;
    }

    console.log("TermsOfService: Handling TOS acceptance");
    try {
      await updateUserMutation.mutateAsync({
        data: {
          is_tos_accepted: true,
        },
      });
    } catch (error) {
      console.error("Failed to update TOS acceptance:", error);
    }
  };

  // Show loading state while checking user data
  if (isLoading || !userData) {
    console.log("TermsOfService: Loading or no user data");
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
        <div className="text-center">{t("common.loading")}</div>
      </div>
    );
  }

  // If TOS is already accepted, redirect (ProtectedRoute will handle this)
  if (userData.is_tos_accepted !== false) {
    console.log("TermsOfService: TOS already accepted");
    return null;
  }

  console.log("TermsOfService: Rendering TOS page");
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <div className="mt-2 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
          <div className="sm:mx-auto sm:w-full sm:max-w-4xl">
            <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">
              {t("tos.title")}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t("tos.description")}
            </p>
          </div>

          <div className="mt-8 space-y-6">
            <div>
              <div className="flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    id="tos"
                    name="tos"
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => {
                      setAccepted(e.target.checked);
                    }}
                    className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="tos" className="text-sm text-gray-500">
                    {t("auth.signup.tosAgreement", {
                      termsOfUseLink: (
                        <a
                          href="https://www.loyaltify.io/terms-of-use/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {t("auth.signup.termsOfUse")}
                        </a>
                      ),
                      privacyPolicyLink: (
                        <a
                          href="https://www.loyaltify.io/privacy-policy/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {t("auth.signup.privacyPolicy")}
                        </a>
                      ),
                    })}
                  </label>
                </div>
              </div>
            </div>

            <button
              onClick={handleAccept}
              disabled={!accepted || updateUserMutation.isPending}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white ${accepted ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-400 cursor-not-allowed"}`}
            >
              {updateUserMutation.isPending
                ? t("common.loading")
                : t("tos.acceptButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
